import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sprout, Activity, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase colors matching FarmOverview - YOUR 45-day schedule
const PHASE_CONFIG = {
  idle: { label: 'Idle', color: 'bg-gray-200', textColor: 'text-gray-700', border: 'border-gray-300' },
  spawning: { label: 'Spawning', color: 'bg-emerald-500', textColor: 'text-white', border: 'border-emerald-600' },
  spawn_run: { label: 'Spawn Run', color: 'bg-green-400', textColor: 'text-white', border: 'border-green-500' },
  casing: { label: 'Casing', color: 'bg-blue-500', textColor: 'text-white', border: 'border-blue-600' },
  case_run: { label: 'Case Run', color: 'bg-cyan-400', textColor: 'text-white', border: 'border-cyan-500' },
  ruffling: { label: 'Ruffling', color: 'bg-indigo-500', textColor: 'text-white', border: 'border-indigo-600' },
  recovery: { label: 'Recovery', color: 'bg-violet-500', textColor: 'text-white', border: 'border-violet-600' },
  covering: { label: 'Covering', color: 'bg-teal-500', textColor: 'text-white', border: 'border-teal-600' },
  airing: { label: 'Airing', color: 'bg-lime-500', textColor: 'text-white', border: 'border-lime-600' },
  flush_1: { label: 'Flush 1', color: 'bg-yellow-400', textColor: 'text-yellow-900', border: 'border-yellow-500' },
  rest: { label: 'Rest', color: 'bg-gray-400', textColor: 'text-white', border: 'border-gray-500' },
  flush_2: { label: 'Flush 2', color: 'bg-orange-500', textColor: 'text-white', border: 'border-orange-600' },
  completed: { label: 'Completed', color: 'bg-gray-300', textColor: 'text-gray-600', border: 'border-gray-400' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-200', textColor: 'text-blue-700', border: 'border-blue-300' },
};

const QuickStats = () => {
  const [phaseStats, setPhaseStats] = useState({});
  const [phaseRooms, setPhaseRooms] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalRooms, setTotalRooms] = useState(0);
  const [activeRooms, setActiveRooms] = useState(0);
  const [selectedPhase, setSelectedPhase] = useState(null);

  useEffect(() => {
    const fetchPhaseStats = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Get all room cycles to calculate phase distribution
        const [cyclesRes, roomsRes] = await Promise.all([
          axios.get(`${API}/room-cycles?status=active`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        // Handle both array and object with 'cycles' key response formats
        const cycles = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
        const rooms = roomsRes.data;
        
        setTotalRooms(rooms.length);
        
        // Create room map for quick lookup
        const roomMap = {};
        rooms.forEach(r => {
          roomMap[r.id] = r.name;
        });
        
        // Normalize phase name to match PHASE_CONFIG keys
        const normalizePhase = (phase) => {
          if (!phase) return 'spawning';
          // Convert to lowercase and replace spaces with underscores
          const normalized = phase.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
          // Handle special cases
          if (normalized === 'casing_run') return 'case_run';
          if (normalized === 'pre_flush_2') return 'rest';
          return normalized;
        };
        
        // Count rooms by phase and store room names
        const phaseCounts = {};
        const phaseRoomNames = {};
        const roomsWithCycles = new Set();
        
        cycles.forEach(cycle => {
          const phase = normalizePhase(cycle.current_phase);
          phaseCounts[phase] = (phaseCounts[phase] || 0) + 1;
          
          if (!phaseRoomNames[phase]) {
            phaseRoomNames[phase] = [];
          }
          phaseRoomNames[phase].push({
            name: roomMap[cycle.room_id] || 'Unknown',
            day: cycle.current_day,
            batch: cycle.batch_name
          });
          
          roomsWithCycles.add(cycle.room_id);
        });
        
        // Count idle rooms (rooms without active cycles)
        const idleRoomsList = rooms.filter(r => !roomsWithCycles.has(r.id));
        const idleCount = idleRoomsList.length;
        if (idleCount > 0) {
          phaseCounts['idle'] = idleCount;
          phaseRoomNames['idle'] = idleRoomsList.map(r => ({
            name: r.name,
            day: null,
            batch: null
          }));
        }
        
        setActiveRooms(cycles.length);
        setPhaseStats(phaseCounts);
        setPhaseRooms(phaseRoomNames);
      } catch (error) {
        console.error('Failed to fetch phase stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhaseStats();
  }, []);

  if (loading) {
    return (
      <Card className="border-gray-100 shadow-sm" data-testid="quick-stats-loading">
        <CardContent className="py-6">
          <div className="text-center text-gray-500">Loading stats...</div>
        </CardContent>
      </Card>
    );
  }

  // Sort phases by the order they appear in the cycle
  const phaseOrder = ['idle', 'spawning', 'spawn_run', 'casing', 'case_run', 'ruffling', 'covering', 'airing', 'flush_1', 'rest', 'flush_2', 'completed'];
  const sortedPhases = Object.entries(phaseStats).sort((a, b) => {
    return phaseOrder.indexOf(a[0]) - phaseOrder.indexOf(b[0]);
  });

  // Filter to only show phases with rooms
  const activePhases = sortedPhases.filter(([_, count]) => count > 0);

  const handlePhaseClick = (phase) => {
    setSelectedPhase(phase);
  };

  return (
    <>
      <Card className="border-gray-100 shadow-sm" data-testid="quick-stats-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Quick Stats - Room Phases Today
            <span className="text-xs text-gray-400 ml-2">(Click to see rooms)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Summary row */}
          <div className="flex items-center gap-4 mb-4 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Sprout className="w-4 h-4 text-[#1A4D2E]" />
              <span className="text-sm text-gray-600">
                <span className="font-bold text-gray-900">{activeRooms}</span> active / <span className="font-bold text-gray-900">{totalRooms}</span> total rooms
              </span>
            </div>
          </div>
          
          {/* Phase distribution grid - clickable, responsive */}
          <div className="flex flex-wrap gap-2">
            {activePhases.map(([phase, count]) => {
              const config = PHASE_CONFIG[phase] || PHASE_CONFIG.idle;
              return (
                <button
                  key={phase}
                  onClick={() => handlePhaseClick(phase)}
                  className={`flex items-center justify-between p-2 rounded-lg border ${config.border} ${config.color} cursor-pointer hover:opacity-90 hover:scale-105 transition-all min-w-[100px] sm:min-w-[120px]`}
                  data-testid={`phase-stat-${phase}`}
                >
                  <span className={`text-xs font-medium ${config.textColor} truncate`}>
                    {config.label}
                  </span>
                  <span className={`text-lg font-bold ${config.textColor} ml-2`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Production highlight */}
          {(phaseStats['flush_1'] > 0 || phaseStats['flush_2'] > 0) && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Harvesting today:</span>
                <span className="font-bold text-[#1A4D2E]">
                  {(phaseStats['flush_1'] || 0) + (phaseStats['flush_2'] || 0)} rooms
                </span>
                {phaseStats['flush_1'] > 0 && (
                  <button
                    onClick={() => handlePhaseClick('flush_1')}
                    className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 cursor-pointer"
                  >
                    {phaseStats['flush_1']} Flush 1
                  </button>
                )}
                {phaseStats['flush_2'] > 0 && (
                  <button
                    onClick={() => handlePhaseClick('flush_2')}
                    className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800 hover:bg-orange-200 cursor-pointer"
                  >
                    {phaseStats['flush_2']} Flush 2
                  </button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Room Details Modal */}
      {selectedPhase && (
        <Dialog open={true} onOpenChange={() => setSelectedPhase(null)}>
          <DialogContent className="sm:max-w-[400px]" data-testid="phase-rooms-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${PHASE_CONFIG[selectedPhase]?.color || 'bg-gray-400'}`} />
                {PHASE_CONFIG[selectedPhase]?.label || selectedPhase} - {phaseStats[selectedPhase]} Room{phaseStats[selectedPhase] > 1 ? 's' : ''}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-2">
              {phaseRooms[selectedPhase]?.map((room, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${PHASE_CONFIG[selectedPhase]?.border || 'border-gray-300'} bg-gray-50`}
                  data-testid={`room-detail-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">{room.name}</span>
                    {room.day && (
                      <span className="text-sm text-gray-600">Day {room.day}/45</span>
                    )}
                  </div>
                  {room.batch && (
                    <p className="text-xs text-gray-500 mt-1">Batch: {room.batch}</p>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default QuickStats;
